/**
 * ResendWhenPicker — when the re-sent drops go out: now, each at its own
 * planned time, or one picked start — always inside SMSWiser's 24 h window.
 * For Media Devoted | Created By Ciro Autiero
 */

import type { FC } from 'react';
import { CalendarClock, History, Send } from 'lucide-react';
import { RESEND_MIN_LEAD_MS, latestResendStart } from '@trafficpulse/shared';

import { DateTimePicker, SegmentedPills } from '@/components/ui';
import { formatDateTimeDMYHM } from '@/features/smswiser-campaigns/utils/dateFormat';
import { utcIsoToScheduleLocal } from '@/features/smswiser-campaigns/utils/scheduleTimezone';

import type { ResendMode, ResendPlan } from '@/features/resend-killed-drops/planResend';

const MODES = [
  { value: 'now' as const, label: 'Now', icon: Send },
  { value: 'original' as const, label: 'Original time', icon: History },
  { value: 'pick' as const, label: 'Pick a time', icon: CalendarClock },
];

interface ResendWhenPickerProps {
  mode: ResendMode;
  onModeChange: (mode: ResendMode) => void;
  /** Danish wall clock, YYYY-MM-DDTHH:mm. */
  picked: string;
  onPickedChange: (value: string) => void;
  issue: ResendPlan['issue'];
  now: Date;
  disabled?: boolean;
}

function hintFor(mode: ResendMode, latest: string): string {
  if (mode === 'now') return 'Each drop goes out as a new drop as soon as SMSWiser accepts it.';
  if (mode === 'original') {
    return 'Each drop at its own planned time (Danish), the next time that time comes round.';
  }
  return `One start for all, Danish time. SMSWiser accepts up to 24 hours ahead — latest ${latest}.`;
}

function issueText(issue: ResendPlan['issue'], latest: string): string | null {
  if (issue === 'missing') return 'Pick a date and time.';
  if (issue === 'past') return 'That time has passed — pick one at least a minute ahead.';
  if (issue === 'too-far') return `SMSWiser only accepts a start within 24 hours — latest ${latest}.`;
  if (issue === 'invalid') return 'That is not a valid date.';
  return null;
}

export const ResendWhenPicker: FC<ResendWhenPickerProps> = ({
  mode,
  onModeChange,
  picked,
  onPickedChange,
  issue,
  now,
  disabled,
}) => {
  const latest = latestResendStart(now);
  const latestText = formatDateTimeDMYHM(latest);
  const problem = issueText(issue, latestText);
  return (
    <section className="space-y-2" aria-label="When">
      <div className="text-[10.5px] font-semibold uppercase tracking-wide text-muted-foreground">When</div>
      <fieldset disabled={disabled} className="flex flex-wrap items-center gap-2">
        <SegmentedPills options={MODES} value={mode} onChange={onModeChange} />
        {mode === 'pick' && (
          <DateTimePicker
            value={picked}
            onChange={onPickedChange}
            min={utcIsoToScheduleLocal(new Date(now.getTime() + RESEND_MIN_LEAD_MS).toISOString())}
            max={utcIsoToScheduleLocal(latest.toISOString())}
            minDateToday
            placeholder="dd/mm/yyyy, --:--"
            ariaLabel="Re-send start"
            className="w-56"
          />
        )}
      </fieldset>
      <p className="text-xs text-muted-foreground">{hintFor(mode, latestText)}</p>
      {problem && <p className="text-xs font-medium text-destructive">{problem}</p>}
    </section>
  );
};
